import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToastMessage } from 'hooks';
import InventoryCompletionExceptionControl from './InventoryCompletionExceptionControl';
import {
	getInventoryCompletionRestrictions,
	updateInventoryCompletionRestrictions,
} from './inventoryCompletionExceptionApi';

export const getInventoryRestrictionErrorMessage = error => {
	const value = Array.isArray(error) ? error[0] : error || {};
	if (value.httpStatus === 403)
		return 'No tiene autorización para gestionar excepciones de inventario.';
	if (value.httpStatus === 404)
		return 'La orden no está disponible dentro de su alcance.';
	if (value.httpStatus === 409)
		return (
			value.message || 'La orden cambió y la excepción ya no se puede aplicar.'
		);
	if (value.httpStatus === 400)
		return (
			value.message || 'Revise la justificación y las reglas seleccionadas.'
		);
	return value.message || 'No se pudo gestionar la excepción de inventario.';
};

export default function InventoryCompletionException({
	orderId,
	isActive,
	onChanged,
}) {
	const [loadState, setLoadState] = useState({
		orderId,
		management: undefined,
		isLoading: Boolean(orderId),
		loadError: undefined,
	});
	const sendToast = useToastMessage();
	const mountedRef = useRef(true);
	const orderIdRef = useRef(orderId);
	const requestGenerationRef = useRef(0);
	const sendToastRef = useRef(sendToast);
	const onChangedRef = useRef(onChanged);
	const pendingMutationOrderIdsRef = useRef(new Set());
	const previousLoadContextRef = useRef();
	orderIdRef.current = orderId;
	sendToastRef.current = sendToast;
	onChangedRef.current = onChanged;
	const loadStateMatchesOrder = loadState.orderId === orderId;
	const management = loadStateMatchesOrder ? loadState.management : undefined;
	const isLoading = loadStateMatchesOrder
		? loadState.isLoading
		: Boolean(orderId);
	const loadError = loadStateMatchesOrder ? loadState.loadError : undefined;

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			requestGenerationRef.current += 1;
		};
	}, []);

	const loadManagement = useCallback(
		(
			targetOrderId,
			{ notifyError = true, preserveManagement = false } = {},
		) => {
			const generation = requestGenerationRef.current + 1;
			requestGenerationRef.current = generation;
			if (!targetOrderId) {
				setLoadState({
					orderId: targetOrderId,
					management: undefined,
					isLoading: false,
					loadError: undefined,
				});
				return Promise.resolve(undefined);
			}
			setLoadState(current => ({
				orderId: targetOrderId,
				management:
					preserveManagement && current.orderId === targetOrderId
						? current.management
						: undefined,
				isLoading: true,
				loadError: undefined,
			}));
			return getInventoryCompletionRestrictions(targetOrderId)
				.then(response => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== targetOrderId ||
						requestGenerationRef.current !== generation
					)
						return undefined;
					setLoadState({
						orderId: targetOrderId,
						management: response,
						isLoading: false,
						loadError: undefined,
					});
					const mutationKey = String(targetOrderId);
					if (pendingMutationOrderIdsRef.current.has(mutationKey)) {
						pendingMutationOrderIdsRef.current.delete(mutationKey);
						sendToastRef.current({
							message: 'La excepción de inventario se actualizó correctamente.',
							type: 'success',
						});
						if (onChangedRef.current) onChangedRef.current(targetOrderId);
					}
					return response;
				})
				.catch(error => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== targetOrderId ||
						requestGenerationRef.current !== generation
					)
						return undefined;
					const message = getInventoryRestrictionErrorMessage(error);
					setLoadState(current => ({
						orderId: targetOrderId,
						management:
							preserveManagement && current.orderId === targetOrderId
								? current.management
								: undefined,
						isLoading: false,
						loadError: message,
					}));
					if (notifyError)
						sendToastRef.current({
							message,
							type: 'error',
						});
					return undefined;
				});
		},
		[],
	);

	useEffect(() => {
		const previous = previousLoadContextRef.current;
		const isInitialLoad = !previous;
		const orderChanged = Boolean(previous) && previous.orderId !== orderId;
		const becameActive =
			Boolean(previous) &&
			previous.orderId === orderId &&
			previous.isActive !== true &&
			isActive === true;
		previousLoadContextRef.current = { orderId, isActive };
		if (!isInitialLoad && !orderChanged && !becameActive) return undefined;

		loadManagement(orderId);
		return () => {
			requestGenerationRef.current += 1;
		};
	}, [isActive, loadManagement, orderId]);

	const handleSubmit = async payload => {
		const submittedOrderId = orderId;
		try {
			await updateInventoryCompletionRestrictions(submittedOrderId, payload);
		} catch (error) {
			if (!mountedRef.current || orderIdRef.current !== submittedOrderId)
				return undefined;
			const message = getInventoryRestrictionErrorMessage(error);
			sendToastRef.current({ message, type: 'error' });
			await loadManagement(submittedOrderId, {
				notifyError: false,
				preserveManagement: true,
			});
			throw error;
		}
		if (!mountedRef.current || orderIdRef.current !== submittedOrderId)
			return undefined;

		pendingMutationOrderIdsRef.current.add(String(submittedOrderId));
		return loadManagement(submittedOrderId);
	};

	return (
		<InventoryCompletionExceptionControl
			key={orderId}
			management={management}
			isLoading={isLoading}
			loadError={loadError}
			onRetry={() =>
				loadManagement(orderId, {
					preserveManagement: Boolean(management),
				})
			}
			onSubmit={handleSubmit}
		/>
	);
}
